import { SizedBox } from '@/components/SizedBox'
import { getUserCenterPageRoute, PageRoutes, UserCenterRoutes } from '@/constants/PageRoutes'
import { ButtonType, tabs, useContractOrdererStore } from '@/hooks/use-contract-orderer'
import { CaretBottom } from '@element-plus/icons-vue'
import classNames from 'classnames'
import { ElCheckbox, ElDropdown, ElDropdownItem, ElDropdownMenu, ElForm, ElFormItem, ElIcon } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { useResponsive, useUserInfoStore } from 'store'
import { AssetTransferType, CustomButton, HocElSlider, InputView, PlusIcon, ToggleIcon, UiUtils } from 'ui-components'
import '../styles/orderer-buy-sell-layout.scss'
import { Leverage } from './Leverage'
import { PendingOrderPreference } from './PendingOrderPreference'
import { PositionType } from './PositionType'

export const OrdererBuy = defineComponent({
  props: {
    activeTab: {
      type: String as PropType<'buy' | 'sell' | undefined>,
      default: 'buy',
    },
    onClose: {
      type: Function as PropType<() => void>,
      default: () => {},
      required: false,
    },
  },
  setup(props) {
    const { t } = useI18n()
    const responsive = useResponsive()
    const orderer = useContractOrdererStore()
    const userInfoStore = useUserInfoStore()
    const router = useRouter()
    const formatTooltip = (value: number) => {
      return `${value}%`
    }

    const onTransfer = () => {
      UiUtils.showAssetTransfer({
        fromAssetType: AssetTransferType.Coin,
        toAssetType: AssetTransferType.Contract,
        onRecharge: () => {
          window.open(getUserCenterPageRoute(UserCenterRoutes.Recharge), '_blank')
        },
      })
    }

    // 是否显示开多按钮
    const isShowBuy = computed(() => !responsive.isMobile || props.activeTab === 'buy')

    // 是否显示开空按钮
    const isShowSell = computed(() => !responsive.isMobile || props.activeTab === 'sell')

    const gotoLogin = () => {
      router.push({
        path: PageRoutes.Login,
      })
    }
    const gotoRegister = () => {
      router.push({
        path: PageRoutes.Register,
        query: {
          entry: 'contract_trade',
        },
      })
    }

    return () => (
      <div class={'contract-trade-orderer-buy-sell-layout buy'}>
        <SizedBox height={8} />
        <div class="orderer-info">
          <PositionType />
          <SizedBox width={8} />
          <Leverage />
        </div>
        <SizedBox height={16} />

        <div class="order-tabs">
          {tabs.map((tab, index) => (
            <div class={classNames('order-tab', { active: orderer.activeIndex === index })} onClick={() => orderer.setActiveIndex(index, tab.value)}>
              <span class="order-tab-name">{t(orderer.getTabName(index) || '')}</span>
              {tab.children && tab.children.length > 0 && (
                <ElDropdown teleported={false} trigger={'click'}>
                  {{
                    default: () => (
                      <ElIcon color="var(--colors-text-secondary)">
                        <CaretBottom />
                      </ElIcon>
                    ),
                    dropdown: () => (
                      <ElDropdownMenu class="order-tab-dropdown">
                        {tab.children?.map((child) => (
                          <ElDropdownItem class="order-tab-dropdown-item" onClick={() => orderer.setActiveIndex(index, child.value)}>
                            {t(child.name)}
                          </ElDropdownItem>
                        ))}
                      </ElDropdownMenu>
                    ),
                  }}
                </ElDropdown>
              )}
              <div class="order-tab-line"></div>
            </div>
          ))}
        </div>

        <ElForm labelPosition="top">
          {orderer.activeType !== ButtonType.Iceberg && (
            <ElFormItem label={t('价格')}>
              <InputView
                radius={6}
                height={40}
                isFloatNumber={orderer.activeType !== ButtonType.Market}
                placeholder={t(orderer.activeType === ButtonType.Market ? '市场价格' : '价格')}
                suffixIcon={() => 'USDT'}
                disabled={orderer.activeType === ButtonType.Market}
                v-model={orderer.limitPrice}
              />
            </ElFormItem>
          )}
          <ElFormItem label={t('数量')}>
            <InputView
              radius={6}
              height={40}
              isFloatNumber={orderer.isAmountFocus}
              placeholder={t('数量')}
              suffixIcon={() => (
                <div class="toggle-icon" onClick={orderer.toggleAmountType}>
                  <ToggleIcon />
                  <span>{orderer.currentOpenCoinName}</span>
                </div>
              )}
              modelValue={orderer.buyAmount}
              onFocus={orderer.onAmountFocus}
              onBlur={orderer.onAmountBlur}
              onInput={orderer.onAmountInput}
            />
          </ElFormItem>

          <HocElSlider v-model={orderer.slider} marks={orderer.sliderMarks} formatTooltip={formatTooltip} onInput={(value) => orderer.onMarginSliderChange(value as number)} />
          <div class="other-info">
            <div class="other-info-cell">
              <span class="label">{t('可用保证金')}</span>
              <div class={'value'}>
                <span>{orderer.availBalance === '--' ? '--' : orderer.availBalance?.formatNumberWithCommas().decimalPadZero(2)} USDT</span>
                <PlusIcon onClick={onTransfer} />
              </div>
            </div>
            {orderer.activeType !== ButtonType.Iceberg && (
              <div class="other-info-cell">
                <span class="label">{t('赠金')}</span>
                <div class={'value'}>
                  <span>
                    {orderer.bonusAssets} {orderer.settleName}
                  </span>
                </div>
              </div>
            )}
          </div>
          {orderer.activeType === ButtonType.Iceberg && (
            <div class="iceberg-form">
              <ElFormItem label={''}>
                <InputView v-model={orderer.iceCount} radius={6} height={40} isNumber placeholder={t('拆分单数')} suffixIcon={() => '<=20'} />
              </ElFormItem>
              <div class="other-info single-amount">
                <div class="other-info-cell">
                  <span class="label">{t('单笔数量')}</span>
                  <div class={'value'}>
                    <span>
                      {orderer.iceSingleAmount} {orderer.currentOpenCoinName}
                    </span>
                  </div>
                </div>
              </div>
              <ElFormItem label={''}>
                <PendingOrderPreference />
              </ElFormItem>
              {/* 触发价格 */}
              <ElCheckbox v-model={orderer.isCheckedIceTriggerPrice}>{t('触发价格')}</ElCheckbox>
              {orderer.isCheckedIceTriggerPrice && <InputView v-model={orderer.iceTriggerPrice} radius={6} height={40} isFloatNumber placeholder={t('触发价格')} />}
            </div>
          )}
          {/* 止盈止损 */}
          {orderer.activeType !== ButtonType.Iceberg && (
            <div class="stop-loss-take-profit">
              <div>
                <ElCheckbox v-model={orderer.isCheckedStopLossTakeProfit}>{t('止盈止损')}</ElCheckbox>
              </div>
              {orderer.isCheckedStopLossTakeProfit && (
                <div class="stop-loss-take-profit-form">
                  <ElFormItem label={t('止盈价格')}>
                    <InputView v-model={orderer.takeProfitPrice} radius={6} height={40} isFloatNumber maxDecimalLength={2} placeholder={t('请输入止盈价格')} />
                  </ElFormItem>
                  <ElFormItem label={t('止损价格')}>
                    <InputView v-model={orderer.stopLossPrice} radius={6} height={40} isFloatNumber maxDecimalLength={2} placeholder={t('请输入止损价格')} />
                  </ElFormItem>
                </div>
              )}
            </div>
          )}
        </ElForm>
        <div class="order-button-group">
          {isShowBuy.value && (
            <>
              {userInfoStore.isLogin && (
                <CustomButton
                  radius={6}
                  class={'order-button max-sm:!mt-[12px]'}
                  type={'primary'}
                  primaryColor="var(--colors-up)"
                  onClick={orderer.onOrderBuyWithLong}
                  loading={orderer.longLoading}
                  style="color: #fff"
                >
                  {t('开多')}
                </CustomButton>
              )}
              {!userInfoStore.isLogin && (
                <CustomButton radius={6} style="color: #fff" class={'order-button max-sm:!mt-[12px]'} type={'primary'} primaryColor="var(--colors-up)" onClick={gotoLogin}>
                  {t('登录')}
                </CustomButton>
              )}
              <div class="other-info">
                <div class="other-info-cell">
                  <span class="label">{t('可开')}</span>
                  <div class={'value'}>
                    <span>
                      {orderer.longGetAmountInputValue.formatNumberWithCommas() || '--'} {orderer.currentOpenCoinName}
                    </span>
                  </div>
                </div>
                <div class="other-info-cell">
                  <span class="label">{t('保证金')}</span>
                  <div class={'value'}>
                    <span>{userInfoStore.isLogin ? orderer.longBailValue : '--'} USDT</span>
                  </div>
                </div>
              </div>
            </>
          )}
          {isShowSell.value && (
            <>
              {userInfoStore.isLogin && (
                <CustomButton
                  radius={6}
                  class={'order-button short'}
                  type={'primary'}
                  primaryColor="var(--colors-down)"
                  onClick={orderer.onOrderBuyWithShort}
                  loading={orderer.shortLoading}
                  style="color: #fff"
                >
                  {t('开空')}
                </CustomButton>
              )}
              {!userInfoStore.isLogin && (
                <CustomButton radius={6} class={'order-button short'} type={'primary'} primaryColor="var(--colors-down)" style="color: #fff" onClick={gotoRegister}>
                  {t('注册')}
                </CustomButton>
              )}
              <div class="other-info">
                <div class="other-info-cell">
                  <span class="label">{t('可开')}</span>
                  <div class={'value'}>
                    <span>
                      {orderer.shortGetAmountInputValue.formatNumberWithCommas() || '--'} {orderer.currentOpenCoinName}
                    </span>
                  </div>
                </div>
                <div class="other-info-cell">
                  <span class="label">{t('保证金')}</span>
                  <div class={'value'}>
                    <span>{userInfoStore.isLogin ? orderer.shortBailValue : '--'} USDT</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    )
  },
})
